/**
 * Step `build_cost` (SA-1197, spec §5.1): the ids the view binds to, its
 * contract check and its reads of the task. Pure: no React, no fetch, because
 * the manifest imports `matchesBuildCost` eagerly. It never reads the pin text
 * (SA-859 D5): the server's `parse_reference` is its one reader.
 */
import type { TaskRead } from '@/types/process'
import { COST_SHEET, COST_SHEET_COLUMNS, COST_SHEET_REFERENCES, PRICED_AS, carries } from '../contract'
import type { CostSheetRow } from '../types'
import type { SectorIn } from './types'

export const STEP_BUILD_COST = 'build_cost'
export const F_COST_SHEET = COST_SHEET
export const F_DOC = 'direct_operating_cost'
export const F_BLOCK_HOURS = 'block_hours'
/** SA-1203: optional, and absent on a record pinned before it (the view then keeps Price as to itself). */
export const F_PRICED_AS = PRICED_AS
export const NEED_REFERENCE = 'study_reference'
export const NEED_SECTORS = 'sectors'
export const NEED_AIRCRAFT_TYPE = 'aircraft_type'
export const EXIT_DONE = 'done'
export const EXIT_NEW_STUDY = 'new_study'

const FORM = [F_COST_SHEET, F_DOC, F_BLOCK_HOURS] as const
/** The column lists a pinned version may declare, each the contract's leading columns in order:
 * SA-1197's thirteen, SA-1209's seventeen (the four provenance columns after them) and SA-1232's
 * eighteen (`burn_ref` last). A record keeps the list of the version it pins. */
const ACCEPTED_COLUMNS = [13, 17, 18].map((n) => COST_SHEET_COLUMNS.slice(0, n).join())

/** The view can do its job: the cost sheet is the table with one of the accepted column lists and its
 * two reference links, the two figures are numbers, both exits are offered and the sectors are among
 * the needs (plus `carries`' rules: nothing redacted, no AI proposal). A task on a record pinned to the
 * `cost_lines` version keeps the generated form. */
export function matchesBuildCost(task: TaskRead): boolean {
  if (!carries(task, FORM, [EXIT_DONE, EXIT_NEW_STUDY])) return false
  const byId = (id: string) => task.form.find((f) => f.id === id)
  const sheet = byId(F_COST_SHEET)
  const columns = sheet?.columns ?? []
  const refs = Object.fromEntries(columns.filter((c) => c.reference).map((c) => [c.id, c.reference]))
  return sheet?.type === 'table'
    && ACCEPTED_COLUMNS.includes(columns.map((c) => c.id).join())
    && JSON.stringify(refs) === JSON.stringify(COST_SHEET_REFERENCES)
    && byId(F_DOC)?.type === 'number' && byId(F_BLOCK_HOURS)?.type === 'number'
    && task.needs.some((n) => n.id === NEED_SECTORS)
}

/** The pin text as typed, trimmed; blank or absent is no reference. */
export function referenceOf(task: TaskRead): string | null {
  const value = task.needs.find((n) => n.id === NEED_REFERENCE)?.value
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

/** The rotation's aircraft type as typed upstream, trimmed; blank, absent or not text is none. */
export function aircraftTypeOf(task: TaskRead): string | null {
  const value = task.needs.find((n) => n.id === NEED_AIRCRAFT_TYPE)?.value
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

/** Step 8's stored `priced_as` (SA-1203), trimmed; blank, absent, not text or no such field is none: the
 * sheet is priced as the offered aircraft. */
export function pricedAsOf(task: TaskRead): string | null {
  const value = task.form.find((f) => f.id === F_PRICED_AS)?.value
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

/** The rows as the task's own sheet declares them: a column the task does not declare is dropped, so a
 * pre-fill's eighteen columns sent to a record pinned to thirteen or seventeen are never refused (spec §6.1). */
export function rowsForTask(rows: CostSheetRow[], task: TaskRead): CostSheetRow[] {
  const declared = new Set((task.form.find((f) => f.id === F_COST_SHEET)?.columns ?? []).map((c) => c.id))
  return rows.map((row) => Object.fromEntries(
    COST_SHEET_COLUMNS.filter((id) => declared.has(id)).map((id) => [id, row[id as keyof CostSheetRow] ?? null]),
  ) as CostSheetRow)
}

/** Step 4's sectors as the prefill route takes them, the payload in tonnes. */
export function sectorsOf(task: TaskRead): SectorIn[] {
  const value = task.needs.find((n) => n.id === NEED_SECTORS)?.value
  if (!Array.isArray(value)) return []
  return value.filter((row): row is Record<string, unknown> => !!row && typeof row === 'object').map((row) => ({
    from: typeof row.from === 'string' ? row.from : null,
    to: typeof row.to === 'string' ? row.to : null,
    flown_as: typeof row.flown_as === 'string' ? row.flown_as : null,
    // The server bounds a payload at 0 to 1000 tonnes; a mistyped figure outside it is sent as none, not as a 422.
    payload: typeof row.payload === 'number' && Number.isFinite(row.payload) && row.payload >= 0 && row.payload <= 1000 ? row.payload : null,
  }))
}

